"use client"

import { useEffect, useState } from "react"
import { getCustomerWallet } from "../../../api/customers/customers"
import { IonContent, IonPage } from "@ionic/react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Avatar,
  Paper,
  IconButton,
  Skeleton,
  Alert,
  Container,
  TextField,
  InputAdornment,
  Fab,
  Badge,
  Divider,
} from "@mui/material"
import {
  AccountBalance,
  Phone,
  Email,
  Business,
  AccountBalanceWallet,
  Search,
  Refresh,
  Add,
  TrendingUp,
  People,
  AttachMoney,
} from "@mui/icons-material"
import { MenuButton } from "../../../components/Menu"

interface CustomerWallet {
  accountNumber: string
  balValue: number
  bankName: string
  branch_id: string
  businessCode: string
  business_id: string
  dateCreated: string
  email: string
  fullname: string
  id: number
  last_updated: string
  merchantName: string
  phone: string
  phoneNumber: string
  seller_id: string
  shopID: string
  status: number
  user_id: string
  walletCode: string
  walletID: string
  walletName: string
  walletType: string
  wallet_balance: string
}

const CustomersReport = () => {
  const [customersData, setCustomersData] = useState<CustomerWallet[]>([])
  const [filteredCustomers, setFilteredCustomers] = useState<CustomerWallet[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState("")

  const fetchCustomerWallet = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await getCustomerWallet()
      console.log(response)
      // Assuming response is an array of customers
      const customers = await response.data || []
      setCustomersData(customers)
      setFilteredCustomers(customers)
    } catch (err) {
      setError("Failed to fetch customers data")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomerWallet()
  }, [])

  useEffect(() => {
    const filtered = customersData?.filter(
      (customer) =>
        customer?.fullname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer?.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer?.phone.includes(searchTerm) ||
        customer?.accountNumber.includes(searchTerm),
    )
    setFilteredCustomers(filtered)
  }, [searchTerm, customersData])

  const formatCurrency = (amount: string | number) => {
    const numAmount = typeof amount === "string" ? Number.parseFloat(amount) : amount
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(numAmount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getStatusColor = (status: number) => {
    return status === 1 ? "success" : "error"
  }

  const getStatusText = (status: number) => {
    return status === 1 ? "Active" : "Inactive"
  }

  const getTotalBalance = () => {
    return customersData.reduce((total, customer) => {
      return total + Number.parseFloat(customer.wallet_balance || "0")
    }, 0)
  }

  const getActiveCustomers = () => {
    return customersData.filter((customer) => customer.status === 1).length
  }

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2)
  }

  if (loading) {
    return (
      <IonPage>
        <IonContent>
          <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ mb: 4 }}>
              <Skeleton variant="text" width={300} height={40} />
              <Skeleton variant="text" width={200} height={20} />
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Skeleton variant="rectangular" height={120} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Skeleton variant="rectangular" height={120} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Skeleton variant="rectangular" height={120} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Skeleton variant="rectangular" height={120} />
              </Grid>
            </Grid>
            <Box sx={{ mt: 4 }}>
              <Grid container spacing={3}>
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <Grid item xs={12} sm={6} lg={4} key={item}>
                    <Skeleton variant="rectangular" height={280} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </IonContent>
      </IonPage>
    )
  }

  if (error) {
    return (
      <IonPage>
        <IonContent>
          <Container maxWidth="xl" sx={{ py: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <IconButton onClick={fetchCustomerWallet} color="primary">
              <Refresh />
            </IconButton>
          </Container>
        </IonContent>
      </IonPage>
    )
  }

  return (
    <IonPage>
      <IonContent>       
           <MenuButton/>
      
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
                  Customer Wallets
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                  Manage and monitor customer wallet accounts
                </Typography>
              </Box>
              <IconButton onClick={fetchCustomerWallet} color="primary" size="large">
                <Refresh />
              </IconButton>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: "center", bgcolor: "primary.main", color: "white" }}>
                  <People sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {customersData.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Customers
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: "center", bgcolor: "success.main", color: "white" }}>
                  <TrendingUp sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {getActiveCustomers()}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Active Customers
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: "center", bgcolor: "info.main", color: "white" }}>
                  <AttachMoney sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize: "1.5rem" }}>
                    {formatCurrency(getTotalBalance())}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Balance
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper sx={{ p: 3, textAlign: "center", bgcolor: "warning.main", color: "white" }}>
                  <AccountBalance sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h4" fontWeight="bold">
                    {new Set(customersData.map((c) => c.bankName)).size}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Partner Banks
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Search Bar */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search customers by name, email, phone, or account number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
          </Box>

          {/* Customers Grid */}
          <Grid container spacing={3}>
            {filteredCustomers.map((customer) => (
              <Grid item xs={12} sm={6} lg={4} key={customer.user_id}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* Customer Header */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: customer.status === 1 ? "success.main" : "error.main",
                          mr: 2,
                          width: 48,
                          height: 48,
                        }}
                      >
                        {getInitials(customer?.fullname)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" noWrap>
                          {customer.fullname}
                        </Typography>
                        <Chip
                          label={getStatusText(customer.status)}
                          color={getStatusColor(customer.status)}
                          size="small"
                        />
                      </Box>
                    </Box>

                    {/* Wallet Balance */}
                    <Box
                      sx={{
                        bgcolor: "primary.light",
                        color: "primary.contrastText",
                        p: 2,
                        borderRadius: 2,
                        mb: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Wallet Balance
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {formatCurrency(customer.wallet_balance)}
                      </Typography>
                    </Box>

                    {/* Customer Details */}
                    <Box sx={{ space: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                        <Email sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} />
                        <Typography variant="body2" noWrap>
                          {customer.email || "No email"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                        <Phone sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} />
                        <Typography variant="body2">{customer.phone}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
                        <AccountBalance sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {customer.bankName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {customer.accountNumber}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Business sx={{ mr: 1, color: "text.secondary", fontSize: 18 }} />
                        <Typography variant="body2" noWrap>
                          {customer.merchantName}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Footer */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Typography variant="caption" color="text.secondary">
                        Created: {formatDate(customer.dateCreated)}
                      </Typography>
                      <Badge
                        badgeContent={customer.walletType === "0" ? "STD" : "PRE"}
                        color={customer.walletType === "0" ? "default" : "primary"}
                      >
                        <AccountBalanceWallet color="action" />
                      </Badge>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No Results */}
          {filteredCustomers.length === 0 && !loading && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No customers found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm ? "Try adjusting your search terms" : "No customer data available"}
              </Typography>
            </Box>
          )}

          {/* Floating Action Button */}
          <Fab
            color="primary"
            aria-label="add customer"
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
            }}
          >
            <Add />
          </Fab>
        </Container>
      </IonContent>
    </IonPage>
  )
}

export default CustomersReport
